// src/components/personal/sections/ClientsSection/components/Table/Table.tsx
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText } from 'lucide-react';
import { Client } from '../../types/types';
import { getStatusColor, getStatusTextStatic } from '../../utils/statusHelpers';
import { TEXT_STYLES } from '@/theme/typography';
import { useBusinessCurrency } from '@/hooks/useBusinessCurrency';
import { formatSmartDate, formatPhoneNumber } from '@/utils/Formatters';
import { LOCALES } from '@/utils/constants';

// Используем оригинальный UITable вместо создания своей реализации
import { Table as UITable, TableColumn } from '@/components/ui/Table/Table';

interface TableProps {
  filteredClients: Client[];
  onClientSelect: (clientId: string) => void;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  loading?: boolean;
  error?: Error | null;
  columnWidths?: Record<string, number>;
  onColumnWidthsChange?: (widths: Record<string, number>) => void;
  sortColumn?: string;
  sortDirection?: 'asc' | 'desc';
  onSort?: (column: string, direction: 'asc' | 'desc') => void;
  scrollable?: boolean;
  autoHeight?: boolean;
  totalItems: number;
  totalPages: number;
}

const Table: React.FC<TableProps> = ({
  filteredClients,
  onClientSelect,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
  loading = false,
  columnWidths,
  onColumnWidthsChange,
  sortColumn,
  sortDirection,
  onSort,
  scrollable = false,
  autoHeight = true,
  totalItems,
  totalPages
}) => {
  const { t } = useTranslation(['clients', 'common', 'errors']);
  const { formatPrice } = useBusinessCurrency();

  // Для отладки параметров пагинации
  console.log('ClientTable - Параметры пагинации:', {
    clientsCount: filteredClients.length,
    currentPage,
    pageSize,
    totalItems,
    totalPages
  });

  // Определение колонок таблицы
  const columns: TableColumn<Client>[] = useMemo(() => [
    { 
      key: 'id', 
      title: t('table.id'), 
      width: 'xs',
      resizable: true,
      render: (client) => (
        <div className="flex items-center">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClientSelect(client.id);
            }}
            className="text-primary hover:text-primary/80 transition-colors duration-200 mr-2"
            title={t('table.open_card')}
          >
            <FileText className="w-4 h-4" />
          </button>
          <div title={t('table.id_with_tooltip', { id: client.id })}>
            {client.id}
          </div>
        </div>
      ),
      mobilePriority: 0 // Самый высокий приоритет
    },
    { 
      key: 'name', 
      title: t('table.name'), 
      width: 'md',
      resizable: true,
      truncate: true,
      maxLength: 30,
      sortable: true,
      mobilePriority: 1
    },
    { 
      key: 'status', 
      title: t('table.status'), 
      width: 'sm',
      resizable: true,
      sortable: true,
      mobilePriority: 2,
      render: (client) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full ${TEXT_STYLES.tiny} ${getStatusColor(client.status)}`}>
          {getStatusTextStatic(client.status, t)}
          {client.statusChange === 'upgrade' && (
            <span className="text-primary ml-1">↑</span>
          )}
          {client.statusChange === 'downgrade' && (
            <span className="text-red-500 ml-1">↓</span>
          )}
        </span>
      )
    },
    { 
      key: 'ordersCount', 
      title: t('table.orders_count'), 
      width: 'xs',
      resizable: true,
      textAlign: 'center',
      sortable: true,
      mobilePriority: 3
    },
    { 
      key: 'totalAmount', 
      title: t('table.total_amount'), 
      width: 'sm',
      resizable: true,
      textAlign: 'right',
      sortable: true,
      mobilePriority: 2,
      render: (client) => (
        <span className="font-medium">
          {formatPrice(client.totalAmount)}
        </span>
      )
    },
    { 
      key: 'phone', 
      title: t('table.phone'),
      width: 'sm',
      resizable: true,
      sortable: true,
      truncate: true,
      maxLength: 15,
      mobilePriority: 1,
      render: (client) => (
        <span>
          {client.phone ? formatPhoneNumber(client.phone) : '—'}
        </span>
      )
    },
    { 
      key: 'lastOrder', 
      title: t('table.last_order'),
      width: 'md',
      resizable: true,
      sortable: true,
      mobilePriority: 3,
      render: (client) => (
        <span>
          {client.lastOrder ? formatSmartDate(client.lastOrder, false, LOCALES.DEFAULT) : '—'}
        </span>
      )
    }
  ], [t, onClientSelect, formatPrice]);

  // Проверка на пустые данные для отображения сообщения

  // ИСПРАВЛЕНО: Добавлены отладочные логи для отслеживания обработчиков пагинации
  const handlePageChangeProxy = (page: number) => {
    console.log(`ClientTable - Вызван handlePageChange с параметром: ${page}`);
    onPageChange(page);
  };

  const handlePageSizeChangeProxy = (size: number) => {
    console.log(`ClientTable - Вызван handlePageSizeChange с параметром: ${size}`);
    onPageSizeChange(size);
  };

  return (
    <div className="w-full">
      {/* Таблица клиентов */}
      <UITable
        data={filteredClients}
        columns={columns}
        onRowClick={(client) => onClientSelect(client.id)}
        isLoading={loading}
        emptyText={t('clients:no_filtered_results')}
        loadingText={t('common:loading')}
        keyExtractor={(client) => client.id}
        scrollable={scrollable}
        autoHeight={autoHeight}
        
        // Параметры для сортировки
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onSort={onSort}
        
        // ИСПРАВЛЕНО: Оставляем встроенную пагинацию отключенной
        // т.к. пагинация реализована в родительском компоненте
        showPagination={false}
        
        // ИСПРАВЛЕНО: Добавляем все необходимые параметры пагинации
        currentPage={currentPage}
        pageSize={pageSize}
        totalItems={totalItems}
        totalPages={totalPages}
        onPageChange={handlePageChangeProxy}
        onPageSizeChange={handlePageSizeChangeProxy}
        
        // Параметры для изменяемых размеров столбцов
        resizableColumns={true}
        savedColumnWidths={columnWidths}
        onColumnWidthsChange={onColumnWidthsChange}
        
        // Адаптивность
        responsive={true}
        mobileBehavior="cards"
        className="w-full"
      />
      
      {/* ВАЖНО: Пагинация вынесена в ClientsSection */}
    </div>
  );
};

export default React.memo(Table);